// Trigger Filter System for Automation Builder
// Allows filtering automation triggers based on specific conditions

// Filter Operators
export type FilterOperator =
  | 'is' // Exact match (=)
  | 'is_not' // Not equal (!=)
  | 'contains' // Partial match (LIKE %value%)
  | 'does_not_contain' // Not contains
  | 'starts_with' // Prefix match (LIKE value%)
  | 'ends_with' // Suffix match (LIKE %value)
  | 'exists' // Field has a value (IS NOT NULL)
  | 'does_not_exist' // Field is empty/null (IS NULL)
  | 'greater_than' // Number comparison (>)
  | 'greater_than_or_equal' // Number comparison (>=)
  | 'less_than' // Number comparison (<)
  | 'less_than_or_equal' // Number comparison (<=)
  | 'between' // Number range (BETWEEN x AND y)
  | 'in' // Value in list (IN [])
  | 'not_in'; // Value not in list (NOT IN [])

// Filter Field Data Types
export type FilterFieldType = 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object';

// Logic Operators for combining multiple filters
export type FilterLogic = 'AND' | 'OR';

// Individual Trigger Filter
export interface TriggerFilter {
  id: string;
  field: string; // JSON path to field (e.g., "addedItem.productPrice")
  fieldLabel: string; // Human-readable label (e.g., "Product Price")
  fieldType: FilterFieldType;
  operator: FilterOperator;
  value?: any; // Comparison value(s) - optional for 'exists' and 'does_not_exist' operators
  logicOperator?: FilterLogic; // How this filter connects to the next one
}

// Filter Group (for complex nested conditions)
export interface TriggerFilterGroup {
  id: string;
  filters: (TriggerFilter | TriggerFilterGroup)[];
  logicOperator: FilterLogic; // AND or OR within this group
}

// Available Fields for Different Trigger Types
export interface FilterFieldDefinition {
  path: string; // JSON path (e.g., "addedItem.productPrice")
  label: string; // Display name (e.g., "Product Price")
  type: FilterFieldType;
  category: string; // Group category (e.g., "Product Details", "Cart Details")
  supportedOperators: FilterOperator[]; // Which operators work with this field
  description?: string; // Help text
  placeholder?: string; // Placeholder for value input
}

// Field definitions for "Added Product to Cart" trigger
export const ADDED_TO_CART_FIELDS: FilterFieldDefinition[] = [
  // Abandoned Checkout
  {
    path: 'abandonedCheckoutURL',
    label: 'Abandoned Checkout URL',
    type: 'string',
    category: 'Checkout',
    supportedOperators: ['exists', 'does_not_exist', 'contains', 'starts_with', 'ends_with']
  },
  
  // Added Item - Product Categories
  {
    path: 'addedItem.productCategories[0].id',
    label: 'Product Category ID',
    type: 'string',
    category: 'Added Item > Product Categories',
    supportedOperators: ['is', 'is_not', 'in', 'not_in', 'exists']
  },
  {
    path: 'addedItem.productCategories[0].title',
    label: 'Product Category Title',
    type: 'string',
    category: 'Added Item > Product Categories',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  },
  
  // Added Item - Product Details
  {
    path: 'addedItem.productDescription',
    label: 'Product Description',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['contains', 'does_not_contain', 'exists', 'does_not_exist']
  },
  {
    path: 'addedItem.productDiscount',
    label: 'Product Discount',
    type: 'number',
    category: 'Added Item',
    supportedOperators: ['exists', 'does_not_exist', 'greater_than', 'less_than', 'is', 'between'],
    placeholder: 'Enter discount amount'
  },
  {
    path: 'addedItem.productID',
    label: 'Product ID',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['is', 'is_not', 'in', 'not_in', 'starts_with']
  },
  {
    path: 'addedItem.productImageURL',
    label: 'Product Image URL',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['exists', 'does_not_exist', 'ends_with']
  },
  {
    path: 'addedItem.productPrice',
    label: 'Product Price',
    type: 'number',
    category: 'Added Item',
    supportedOperators: ['is', 'is_not', 'greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal', 'between'],
    placeholder: 'Enter price'
  },
  {
    path: 'addedItem.productQuantity',
    label: 'Product Quantity',
    type: 'number',
    category: 'Added Item',
    supportedOperators: ['is', 'greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal'],
    placeholder: 'Enter quantity'
  },
  {
    path: 'addedItem.productSKU',
    label: 'Product SKU',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['is', 'is_not', 'starts_with', 'ends_with', 'in', 'not_in']
  },
  {
    path: 'addedItem.productStrikeThroughPrice',
    label: 'Product Strike Through Price',
    type: 'number',
    category: 'Added Item',
    supportedOperators: ['exists', 'does_not_exist', 'greater_than', 'less_than'],
    placeholder: 'Enter original price'
  },
  {
    path: 'addedItem.productTitle',
    label: 'Product Title',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  },
  {
    path: 'addedItem.productURL',
    label: 'Product URL',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['contains', 'starts_with', 'ends_with']
  },
  {
    path: 'addedItem.productVariantID',
    label: 'Product Variant ID',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['is', 'is_not', 'exists', 'does_not_exist']
  },
  {
    path: 'addedItem.productVariantImageURL',
    label: 'Product Variant Image URL',
    type: 'string',
    category: 'Added Item',
    supportedOperators: ['exists', 'does_not_exist', 'ends_with']
  },
  
  // Cart Details
  {
    path: 'cartID',
    label: 'Cart ID',
    type: 'string',
    category: 'Cart Details',
    supportedOperators: ['is', 'is_not']
  },
  {
    path: 'currency',
    label: 'Currency',
    type: 'string',
    category: 'Cart Details',
    supportedOperators: ['is', 'is_not', 'in', 'not_in']
  },
  {
    path: 'value',
    label: 'Cart Value',
    type: 'number',
    category: 'Cart Details',
    supportedOperators: ['is', 'greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal', 'between'],
    placeholder: 'Enter cart total'
  },
  
  // Line Items (all products in cart)
  {
    path: 'lineItems.length',
    label: 'Number of Items in Cart',
    type: 'number',
    category: 'Cart Details',
    supportedOperators: ['is', 'greater_than', 'less_than'],
    placeholder: 'Enter item count'
  }
];

// Get operator label for display
export function getOperatorLabel(operator: FilterOperator): string {
  const labels: Record<FilterOperator, string> = {
    is: 'Is',
    is_not: 'Is not',
    contains: 'Contains',
    does_not_contain: 'Does not contain',
    starts_with: 'Starts with',
    ends_with: 'Ends with',
    exists: 'Exists',
    does_not_exist: 'Does not exist',
    greater_than: 'Greater than',
    greater_than_or_equal: 'Greater than or equal to',
    less_than: 'Less than',
    less_than_or_equal: 'Less than or equal to',
    between: 'Between',
    in: 'In list',
    not_in: 'Not in list'
  };
  return labels[operator];
}

// Get all operators for a field type
export function getOperatorsForFieldType(fieldType: FilterFieldType): FilterOperator[] {
  const operatorsByType: Record<FilterFieldType, FilterOperator[]> = {
    string: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with', 'exists', 'does_not_exist', 'in', 'not_in'],
    number: ['is', 'is_not', 'greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal', 'between', 'exists', 'does_not_exist'],
    boolean: ['is', 'is_not'],
    date: ['is', 'is_not', 'greater_than', 'less_than', 'between'],
    array: ['contains', 'does_not_contain', 'exists', 'does_not_exist'],
    object: ['exists', 'does_not_exist']
  };
  return operatorsByType[fieldType];
}

// Check if operator requires a value input
export function operatorRequiresValue(operator: FilterOperator): boolean {
  return !['exists', 'does_not_exist'].includes(operator);
}

// Check if operator requires two values (range)
export function operatorRequiresTwoValues(operator: FilterOperator): boolean {
  return operator === 'between';
}

// Check if operator requires multiple values (list)
export function operatorRequiresMultipleValues(operator: FilterOperator): boolean {
  return ['in', 'not_in'].includes(operator);
}

